import { useState } from "react";

import { useToast } from "../../../hooks/useToast";
import { serverErrorMessage } from "../../../utils/HttpError";
import {
  FilterButton,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  WarningNotice,
} from "../shared";
import { ModalBodyText } from "./AdminTranslations.styles";

interface IConfirmDeleteMasterModalProps {
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  successMessage: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

/**
 * Confirms a master-store delete (key or single translation) — Retool's
 * `modalDeleteKey` / `modalDeleteTranslation` (EN-5178). Deleting from the
 * master does NOT delete from the environments (Retool behaved the same),
 * hence the fixed warning.
 */
export function ConfirmDeleteMasterModal({
  title,
  body,
  confirmLabel,
  successMessage,
  onConfirm,
  onClose,
}: IConfirmDeleteMasterModalProps) {
  const { showToast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
      showToast(successMessage, "success");
      onClose();
    } catch (error) {
      showToast(
        serverErrorMessage(error) ?? "The delete action failed.",
        "warning"
      );
      setIsDeleting(false);
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>{title}</ModalTitle>
      <ModalForm>
        <ModalBodyText>{body}</ModalBodyText>
        <WarningNotice>
          This only removes it from the master store — environments that already
          synced it keep their copy. This can&apos;t be undone.
        </WarningNotice>
        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isDeleting}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isDeleting}
            onClick={handleConfirm}
          >
            {isDeleting ? "Deleting..." : confirmLabel}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
